import React, { useState, useEffect } from "react";
import { Modal, DatePicker, TimePicker, Radio, notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useThemeColors } from "redux/hooks";

export type ScheduleData = {
    date: Dayjs;
    time: Dayjs;
    frequency: 'daily' | 'weekly' | 'monthly';
    clearSchedule?: boolean;
};

export function ScheduleRegenModalView(props: {
    isOpen: boolean;
    onClose: () => void;
    onSchedule: (schedule: ScheduleData) => Promise<void>;
    darkMode: boolean;
    initialScheduleTime?: string;
    initialScheduleType?: string;
}) {
    const { isOpen, onClose, onSchedule, darkMode, initialScheduleTime, initialScheduleType } = props;
    const themeColors = useThemeColors();
    const [date, setDate] = useState<Dayjs | null>(null);
    const [time, setTime] = useState<Dayjs | null>(null);
    const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily');

    // Initialize form with existing schedule values when modal opens
    useEffect(() => {
        if (isOpen) {
            if (initialScheduleTime && initialScheduleType) {
                const scheduleDate = dayjs(initialScheduleTime);
                setDate(scheduleDate);
                setTime(scheduleDate);
                setFrequency(initialScheduleType as 'daily' | 'weekly' | 'monthly');
            } else {
                setDate(null);
                setTime(null);
                setFrequency('daily');
            }
        }
    }, [isOpen, initialScheduleTime, initialScheduleType]);

    const handleSubmit = async () => {
        if (!date || !time) {
            notification.error({
                message: 'Validation error',
                description: 'Please select both date and time.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        // Combine date and time
        const combinedDateTime = date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);

        // Validate that the scheduled time is in the future
        const now = dayjs();
        if (combinedDateTime.isBefore(now) || combinedDateTime.isSame(now)) {
            notification.error({
                message: 'Invalid schedule time',
                description: 'The scheduled date and time must be in the future.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        try {
            await onSchedule({
                date: combinedDateTime,
                time: time,
                frequency
            });
            onClose();
        } catch (error) {
            // Error handling is done in the onSchedule callback
        }
    };

    const handleStopSchedule = async () => {
        try {
            // Pass a special schedule data that indicates clearing
            await onSchedule({
                date: dayjs(),
                time: dayjs(),
                frequency: 'daily',
                clearSchedule: true
            });
            onClose();
        } catch (error) {
            // Error handling is done in the onSchedule callback
        }
    };

    // Add/remove body class for global dropdown styling
    useEffect(() => {
        if (darkMode && isOpen) {
            document.body.classList.add('schedule-regen-dark-mode');
        } else {
            document.body.classList.remove('schedule-regen-dark-mode');
        }
        return () => {
            document.body.classList.remove('schedule-regen-dark-mode');
        };
    }, [darkMode, isOpen]);

    return (
        <>
            <style>{`
                .schedule-regen-modal .ant-btn-primary {
                    background-color: ${themeColors.primaryColor} !important;
                    border-color: ${themeColors.primaryColor} !important;
                }
                .schedule-regen-modal .ant-btn-primary:hover:not(:disabled) {
                    background-color: ${themeColors.primaryColorHover} !important;
                    border-color: ${themeColors.primaryColorHover} !important;
                }
                .schedule-regen-modal .ant-btn-primary:active:not(:disabled) {
                    background-color: ${themeColors.primaryColorHover} !important;
                    border-color: ${themeColors.primaryColorHover} !important;
                }
                ${darkMode ? `
                    .schedule-regen-modal .ant-modal-content {
                        background-color: #2d2d2d !important;
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-modal-header {
                        background-color: #2d2d2d !important;
                        border-bottom: 1px solid #404040 !important;
                        padding-bottom: 20px !important;
                        margin: 0 -24px 0 -24px !important;
                        padding-left: 24px !important;
                        padding-right: 24px !important;
                    }
                    .schedule-regen-modal .ant-modal-title {
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-modal-close {
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-modal-close:hover {
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-modal-footer {
                        background-color: #2d2d2d !important;
                        border-top: 1px solid #404040 !important;
                        margin: 0 -24px 0 -24px !important;
                        padding-left: 24px !important;
                        padding-right: 24px !important;
                        padding-top: 20px !important;
                    }
                    .schedule-regen-modal .ant-picker {
                        background-color: #1f1f1f !important;
                        border-color: #404040 !important;
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-picker:hover {
                        border-color: #666666 !important;
                    }
                    .schedule-regen-modal .ant-picker-focused {
                        border-color: ${themeColors.primaryColor} !important;
                        box-shadow: 0 0 0 2px ${themeColors.primaryColor}33 !important;
                    }
                    .schedule-regen-modal .ant-picker-input > input {
                        color: #ffffff !important;
                        background-color: transparent !important;
                    }
                    .schedule-regen-modal .ant-picker-input > input::placeholder {
                        color: #888888 !important;
                    }
                    .schedule-regen-modal .ant-picker-suffix {
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-picker-clear {
                        color: #ffffff !important;
                        background-color: transparent !important;
                    }
                    .schedule-regen-modal .ant-radio-wrapper {
                        color: #ffffff !important;
                    }
                    .schedule-regen-modal .ant-radio-inner {
                        border-color: #666666 !important;
                        background-color: #1f1f1f !important;
                    }
                    .schedule-regen-modal .ant-radio-checked .ant-radio-inner {
                        border-color: ${themeColors.primaryColor} !important;
                    }
                    .schedule-regen-modal .ant-radio-checked .ant-radio-inner::after {
                        background-color: ${themeColors.primaryColor} !important;
                    }
                ` : ''}
                /* Constrain picker dropdown width to match input width */
                .schedule-regen-modal .ant-picker-dropdown {
                    min-width: 0 !important;
                    width: auto !important;
                }
                .schedule-regen-modal .ant-picker-dropdown .ant-picker-panel-container {
                    min-width: 0 !important;
                    width: auto !important;
                }
                .schedule-regen-modal .ant-picker-dropdown .ant-picker-panel {
                    min-width: 0 !important;
                    width: auto !important;
                }
                /* Ensure dropdown doesn't extend beyond input width */
                .schedule-regen-modal .ant-picker-dropdown.ant-picker-dropdown-placement-bottomLeft,
                .schedule-regen-modal .ant-picker-dropdown.ant-picker-dropdown-placement-topLeft,
                .schedule-regen-modal .ant-picker-dropdown.ant-picker-dropdown-placement-bottomRight,
                .schedule-regen-modal .ant-picker-dropdown.ant-picker-dropdown-placement-topRight {
                    width: auto !important;
                    min-width: 0 !important;
                }
                /* Global styles for picker dropdowns when modal is open in dark mode */
                body.schedule-regen-dark-mode .ant-picker-dropdown {
                    background-color: #2d2d2d !important;
                    min-width: 0 !important;
                    width: auto !important;
                }
                body.schedule-regen-dark-mode .ant-picker-panel {
                    background-color: #2d2d2d !important;
                    border-color: #404040 !important;
                    min-width: 0 !important;
                    width: auto !important;
                }
                body.schedule-regen-dark-mode .ant-picker-panel-container {
                    background-color: #2d2d2d !important;
                    min-width: 0 !important;
                    width: auto !important;
                }
                body.schedule-regen-dark-mode .ant-picker-header {
                    background-color: #2d2d2d !important;
                    border-bottom-color: #404040 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-header button {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-header button:hover {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-header-view {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-header-view button {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-content {
                    background-color: #2d2d2d !important;
                }
                body.schedule-regen-dark-mode .ant-picker-content th {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell-inner {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell:hover:not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
                    background-color: #404040 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell-selected .ant-picker-cell-inner {
                    background-color: ${themeColors.primaryColor} !important;
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell-today .ant-picker-cell-inner {
                    border-color: ${themeColors.primaryColor} !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell-disabled {
                    color: #666666 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-cell-disabled .ant-picker-cell-inner {
                    color: #666666 !important;
                }
                /* Time picker styles */
                body.schedule-regen-dark-mode .ant-picker-time-panel {
                    background-color: #2d2d2d !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column {
                    border-left-color: #404040 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li * {
                    color: #ffffff !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li:hover {
                    background-color: #404040 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected {
                    background-color: ${themeColors.primaryColor} !important;
                    color: #000000 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected * {
                    color: #000000 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-disabled {
                    color: #666666 !important;
                }
                body.schedule-regen-dark-mode .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-disabled * {
                    color: #666666 !important;
                }
            `}</style>
            <Modal
                className="schedule-regen-modal"
                title={initialScheduleTime && initialScheduleType ? "Edit Schedule" : "Schedule Regeneration"}
                open={isOpen}
                onCancel={onClose}
                onOk={handleSubmit}
                okText={initialScheduleTime && initialScheduleType ? "Edit Schedule" : "Schedule"}
                cancelText="Cancel"
                footer={(_, { OkBtn }) => (
                    <>
                        {initialScheduleTime && initialScheduleType && (
                            <button
                                onClick={handleStopSchedule}
                                style={{
                                    background: darkMode ? "#2d2d2d" : "#ffffff",
                                    border: darkMode ? "1px solid #404040" : "1px solid #d9d9d9",
                                    borderRadius: "6px",
                                    padding: "4px 15px",
                                    height: "32px",
                                    cursor: "pointer",
                                    color: darkMode ? "#ff7875" : "#ff4d4f",
                                    fontSize: "14px",
                                    fontWeight: "400",
                                    marginRight: "8px",
                                    transition: "all 0.2s"
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = darkMode ? "#404040" : "#fafafa";
                                    e.currentTarget.style.borderColor = darkMode ? "#ff7875" : "#ff4d4f";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = darkMode ? "#2d2d2d" : "#ffffff";
                                    e.currentTarget.style.borderColor = darkMode ? "#404040" : "#d9d9d9";
                                }}
                            >
                                Stop Schedule
                            </button>
                        )}
                        <OkBtn />
                    </>
                )}
                styles={{
                    content: {
                        backgroundColor: darkMode ? "#2d2d2d" : "white",
                    },
                    header: {
                        backgroundColor: darkMode ? "#2d2d2d" : "white",
                        borderBottom: darkMode ? "1px solid #404040" : "1px solid #E9E9E9",
                        paddingBottom: "20px",
                        margin: "0 -24px 0 -24px",
                        paddingLeft: "24px",
                        paddingRight: "24px",
                    },
                    footer: {
                        backgroundColor: darkMode ? "#2d2d2d" : "white",
                        borderTop: darkMode ? "1px solid #404040" : "1px solid #E9E9E9",
                        margin: "0 -24px 0 -24px",
                        paddingLeft: "24px",
                        paddingRight: "24px",
                        paddingTop: "20px",
                    }
                }}
            >
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    padding: "20px 0"
                }}>
                    <div style={{ margin: 0 }}>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "15px",
                            fontWeight: "500",
                            color: darkMode ? "#ffffff" : "#333"
                        }}>
                            Date
                        </label>
                    <DatePicker
                        value={date}
                        onChange={(newDate) => {
                            setDate(newDate);
                            // Clear time when date changes to force user to re-select a valid time
                            setTime(null);
                        }}
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                        getPopupContainer={(trigger) => trigger.parentElement || document.body}
                        disabledDate={(current) => {
                            // Disable dates in the past
                            return current && current.isBefore(dayjs(), 'day');
                        }}
                    />
                    </div>

                    <div style={{ margin: 0 }}>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "15px",
                            fontWeight: "500",
                            color: darkMode ? "#ffffff" : "#333"
                        }}>
                            Time
                        </label>
                        <TimePicker
                            value={time}
                            onChange={setTime}
                            style={{ width: "100%" }}
                            format="h:mm A"
                            use12Hours
                            getPopupContainer={(trigger) => trigger.parentElement || document.body}
                            disabledTime={() => {
                                // If today is selected, disable past times
                                if (date && date.isSame(dayjs(), 'day')) {
                                    const now = dayjs();
                                    return {
                                        disabledHours: () => {
                                            const hours: number[] = [];
                                            for (let i = 0; i < now.hour(); i++) {
                                                hours.push(i);
                                            }
                                            return hours;
                                        },
                                        disabledMinutes: (selectedHour: number) => {
                                            // If the selected hour is the current hour, disable past minutes
                                            if (selectedHour === now.hour()) {
                                                const minutes: number[] = [];
                                                for (let i = 0; i <= now.minute(); i++) {
                                                    minutes.push(i);
                                                }
                                                return minutes;
                                            }
                                            return [];
                                        }
                                    };
                                }
                                return {};
                            }}
                        />
                    </div>

                    <div style={{ margin: 0 }}>
                        <label style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "15px",
                            fontWeight: "500",
                            color: darkMode ? "#ffffff" : "#333"
                        }}>
                            Frequency
                        </label>
                        <Radio.Group
                            value={frequency}
                            onChange={(e) => setFrequency(e.target.value)}
                            style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px"
                            }}
                        >
                            <Radio value="daily" style={{ color: darkMode ? "#ffffff" : "#333", margin: 0 }}>
                                Daily
                            </Radio>
                            <Radio value="weekly" style={{ color: darkMode ? "#ffffff" : "#333", margin: 0 }}>
                                Weekly
                            </Radio>
                            <Radio value="monthly" style={{ color: darkMode ? "#ffffff" : "#333", margin: 0 }}>
                                Monthly
                            </Radio>
                        </Radio.Group>
                    </div>
                </div>
            </Modal>
        </>
    );
}

